import {
  CardInReader,
  DeviceStatusRequestIndicator,
  DeviceTypeActionIndicator,
  Encoding,
  FieldType,
  HostGiftStatus,
  HostStatus,
  HostStatusRequestIndicator,
  LastTransactionStatus,
  LastTransactionStatusRequestIndicator,
  PinPadAction1Indicator,
  PinPadAction2Indicator,
  PinPadAction3IndicatorAutoBeepMode,
  PinPadAction3IndicatorContinuousMode,
  PinPadAction3IndicatorSymbologyParameters,
  PinPadAction4Indicator,
  PinPadStatus,
  ResponseDelimiter,
  ReturnVersionRequestIndicator,
  StopProcessingRequestIndicator,
  TerminalStatus,
  TransactionType,
} from '../../constants';
import { IMessage } from '../types';
import { mapKeyToReadableKey, prepare } from '../util';

// tslint:disable:object-literal-sort-keys
// do not sort these keys, their order
// determines the shape of the request
// sent to the pin pad

// device status message should always
// have subtype = 1
const DEVICE_STATUS_SUBTYPE = 1;
/**
 * Device Status can be used for a health check to verify
 * that the client can connect to mcm. It can also be used
 * to request that mcm perform certain actions, including
 * administrative actions like preventing new transactions
 * and cancelling transactions in progress but not all
 * actions are supported for all pin pads
 */
const DeviceStatusSchema = {
  transactionType: {
    length: 2,
    type: FieldType.fixed,
    value: TransactionType.DeviceStatus,
  },
  transactionSubtype: {
    length: 1,
    type: FieldType.fixed,
    value: DEVICE_STATUS_SUBTYPE,
  },
  /**
   * Pin Pad Status request type
   * @see DeviceStatusRequestIndicator
   */
  getPinPadStatus: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Get Host Status flag
   * @see HostStatusRequestIndicator
   */
  getHostStatus: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Stop Processing flag
   * @see StopProcessingRequestIndicator
   */
  stopProcessingFlag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Return version flag
   * @see ReturnVersionRequestIndicator
   */
  returnVersionFlag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Last Transaction Status flag
   * @see LastTransactionStatusRequestIndicator
   */
  lastTransactionFlag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Pin Pad Action Flag
   * @see DeviceTypeActionIndicator
   */
  deviceTypeActionFlag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Pin Pad Action 1 flag
   * Only available for "Device Type 2" (E355)
   * @see PinPadAction1Indicator
   */
  pinPadAction1Flag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Pin Pad Action 2 flag
   * Only available when pinPadAction1Flag
   * is set to PinPadActionIndicator.ChangeConfiguration
   * @see PinPadAction2Indicator
   */
  pinPadAction2Flag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * Pin Pad Action3 flag
   * Used in conjunction with Action2 to enable and
   * disable barcode scanner configurations
   *
   * @see PinPadAction3IndicatorContinuousMode
   * @see PinPadAction3IndicatorAutoBeepMode
   * @see PinPadAction3IndicatorSymbologyParameters
   */
  pinPadAction3Flag: {
    length: 2,
    type: FieldType.numeric,
  },
  /**
   * Pin Pad Action4 flag
   * Used in conjunction with
   * PinPadAction3IndicatorSymbologyParameters to
   * enable/disable the scanning of the given
   * barcode symbology. supporting more of
   * symbologies may impact scanner performance
   * @see PinPadAction4Indicator
   */
  pinPadAction4Flag: {
    length: 1,
    type: FieldType.numeric,
  },
  /**
   * reserved field
   */
  empty0: {
    length: 55,
    type: FieldType.reserved,
  },
  /**
   * terminal ID
   */
  terminalId: {
    length: 12,
    type: FieldType.numeric,
  },
};
// tslint:enable:object-literal-sort-keys

export interface IDeviceStatusInput {
  deviceTypeActionFlag?: DeviceTypeActionIndicator;
  getHostStatus: HostStatusRequestIndicator;
  getPinPadStatus: DeviceStatusRequestIndicator;
  lastTransactionFlag?: LastTransactionStatusRequestIndicator;
  pinPadAction1Flag?: PinPadAction1Indicator;
  pinPadAction2Flag?: PinPadAction2Indicator;
  pinPadAction3Flag?:
    | PinPadAction3IndicatorAutoBeepMode
    | PinPadAction3IndicatorContinuousMode
    | PinPadAction3IndicatorSymbologyParameters;
  pinPadAction4Flag?: PinPadAction4Indicator;
  returnVersionFlag?: ReturnVersionRequestIndicator;
  stopProcessingFlag?: StopProcessingRequestIndicator;
  terminalId: string;
}

export const deviceStatus = ({
  deviceTypeActionFlag = DeviceTypeActionIndicator.NoDeviceSelected,
  getHostStatus,
  getPinPadStatus,
  lastTransactionFlag = LastTransactionStatusRequestIndicator.NoAction,
  pinPadAction1Flag = PinPadAction1Indicator.NoAction,
  pinPadAction2Flag = PinPadAction2Indicator.NoAction,
  pinPadAction3Flag = PinPadAction3IndicatorAutoBeepMode.Disable,
  pinPadAction4Flag = PinPadAction4Indicator.Disable,
  returnVersionFlag = ReturnVersionRequestIndicator.NoAction,
  stopProcessingFlag = StopProcessingRequestIndicator.NoAction,
  terminalId,
}: IDeviceStatusInput) => {
  return prepare(
    {
      deviceTypeActionFlag,
      getHostStatus,
      getPinPadStatus,
      lastTransactionFlag,
      pinPadAction1Flag,
      pinPadAction2Flag,
      pinPadAction3Flag,
      pinPadAction4Flag,
      returnVersionFlag,
      stopProcessingFlag,
      terminalId,
    } as IMessage<typeof DeviceStatusSchema>,
    DeviceStatusSchema
  );
};

export interface IDeviceStatusResponse {
  applicationVersion: string;
  cardInReader: CardInReader;
  cdcaMDllVersion: string;
  coreLibraryVersion: string;
  deviceModelName: string;
  emvKernelVersion: string;
  hostDriverVersion: string;
  hostGiftStatus: HostGiftStatus;
  hostStatus: HostStatus;
  lastTransactionStatus: LastTransactionStatus;
  multiExeVersion: string;
  pinPadName: string;
  pinPadSerialNumber: string;
  pinPadStatus: PinPadStatus;
  remainingDeviceAutoRebootTime: number;
  romOsVersion: string;
  terminalStatus: TerminalStatus;
}

export const parseResponse = (data: Buffer): IDeviceStatusResponse => {
  const responseString = data.toString(Encoding.ascii);

  const [, rcpLine, ...responseLines] = responseString.split(ResponseDelimiter.CRLF);

  const [, , trmLine] = rcpLine.split(ResponseDelimiter.FS);

  const [, terminalId] = trmLine.split(ResponseDelimiter.Colon);

  const formattedResponse = responseLines
    .slice(0, responseLines.length - 2)
    .reduce<IDeviceStatusResponse>((acc, line) => {
      const [key, value] = line.split(ResponseDelimiter.Colon) as [
        keyof IDeviceStatusResponse,
        string
      ];

      const intValue = parseInt(value, 10);

      const readableKey = mapKeyToReadableKey(key);

      return {
        ...acc,
        [readableKey]: !isNaN(intValue) ? intValue : value,
      };
    }, {} as IDeviceStatusResponse);

  return Object.assign({}, { terminalId }, formattedResponse, { rawResponse: responseString });
};
